import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  HStack,
  IconButton,
  Image,
  Link as ChakraLink,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  SkeletonText,
  Text,
  VStack,
} from '@chakra-ui/react';

import { ChatChartBlock, ChatFigureBlock, ChatMermaidBlock } from './chatBlocks';
import { parseFigureSrc } from './figureSrc';

import { Link as RouterLink } from 'react-router';
import { BowerIcon } from '../../ui/BowerIcon';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { BirdStatusLine } from './BirdStatusLine';
import ReactMarkdown from 'react-markdown';

import { CHAT_REHYPE_PLUGINS, CHAT_REMARK_PLUGINS } from './chatMarkdownPlugins';
import type { Element as HastElement } from 'hast';

import { ChatCodeBlock, readFencedCode, splitFencedSegments } from './chatCode';
import { preprocessCitationPlacement } from './chatCitations';
import { normalizeDisplayMathDelimiters } from './mathDelimiters';
import { ToolChoicePrompt } from './ToolChoicePrompt';
import { ToolConfirmationPrompt } from './ToolConfirmationPrompt';
import type { FencedSegment } from './chatCode';
import { MarkdownTable } from './MarkdownTable';
import { StatusPill } from './StatusPill';
import { statusToneFor } from './markdownTable.utils';
import { AgentSpreadsheetChangeCard } from './AgentSpreadsheetChangeCard';
import { MessageSources } from './MessageSources';
import { getFaviconUrl, getSafeDomain, isInternalUrl, useUrlMetadata } from './sourceUrls';
import { EntityLink, ExternalLink, type InternalLinkPolicy } from './EntityLink';
import { parseEntityHref } from './entityHref';

import type { GroundingSource } from '../../../services/agentChat.service';
import { getDisplayTitle } from '../../../utils/artifactUtils';
import { ARTIFACT_TYPE_CONFIG } from '@bower/shared/constants/artifact-types.generated';
import { submitChatFeedback } from '../../../services/agentChat.service';
import type { ChatMessage } from '../../../hooks/data/useAgentChat';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { loadKatexStyles } from '../../../utils/katexStyles';
import { safeAnalytics } from '../../../utils/analytics';
import { USER_BUBBLE } from '../../ui/userBubble';

interface BirdMessageProps {
  message: ChatMessage;
  readonly internalLinkPolicy?: InternalLinkPolicy;
  /**
   * Answer a tool call ADK paused for approval. Absent on surfaces that render
   * history rather than a live turn — a replayed confirmation is already
   * resolved, so it renders as prose with no buttons.
   */
  readonly onRespondToConfirmation?: (callId: string, confirmed: boolean) => void;
  /** The confirmation whose reply is in flight, if any — locks its buttons. */
  readonly respondingConfirmationId?: string | null;
  readonly onRespondToChoice?: (callId: string, value: string) => void;
  readonly respondingChoiceId?: string | null;
}

function getAttachmentIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'heic'].includes(ext)) return 'image';
  if (['mp3', 'wav', 'm4a', 'ogg', 'flac'].includes(ext)) return 'audio_file';
  if (['mp4', 'mov', 'webm', 'avi', 'mkv'].includes(ext)) return 'video_file';
  if (['pdf', 'txt', 'md', 'csv', 'rtf'].includes(ext)) return 'article';
  // Default falls back to the registry icon so a single rename propagates.
  return ARTIFACT_TYPE_CONFIG.attachment.icon;
}

function MessageAttachments({ attachments }: { attachments: { name: string }[] }) {
  return (
    <HStack spacing={1.5} mt={2} flexWrap="wrap">
      {attachments.map((a, i) => (
        <HStack
          key={`${a.name}-${i}`}
          spacing={1}
          px={2}
          py={1}
          bg="bg.surface"
          borderRadius="md"
          border="1px solid"
          borderColor="border.default"
          maxW="fit-content"
        >
          <MaterialIcon name={getAttachmentIcon(a.name)} boxSize={3.5} gradient="warm" />
          <Text fontSize="xs" color="text.primary" fontWeight="medium" noOfLines={1} maxW="200px">
            {a.name}
          </Text>
        </HStack>
      ))}
    </HStack>
  );
}

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Match citation markers like [1], [2] inserted by the backend. */
const CITATION_RE = /(\[\d+\])/g;

/**
 * NOT a `Badge`. A lozenge stretches to its label; this is a fixed 16x16 disc
 * (`w`/`h`, not `minW`) carrying a superscript citation marker, and it must
 * stay one at every index so a paragraph of `[1][2][3]` reads as a row of
 * evenly-sized marks. `MessageSources` draws the matching disc for the source
 * list. The count badge on `ConnectorPickerButton` is the other case and
 * resolves the other way — it uses `minW`, so it is a pill that happens to be
 * round at one digit.
 */
function CitationLabel({ num }: { readonly num: number }) {
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      w={4}
      h={4}
      fontSize="2xs"
      fontWeight="semibold"
      color="brand.onSurface"
      bg="brand.surface"
      borderRadius="full"
      verticalAlign="super"
      lineHeight="1"
      ml={0.5}
    >
      {num}
    </Box>
  );
}

/** Citation badge with hover popover showing source details */
function NavigableCitationBadge({ num, source }: { num: number; source: GroundingSource }) {
  const [isOpen, setIsOpen] = useState(false);
  const internal = isInternalUrl(source.url);
  const { metadata, isLoading } = useUrlMetadata(source.url, isOpen);

  const linkUrl = metadata?.url || source.url;
  const displayTitle = metadata?.title || source.title;
  const displayDomain = metadata?.domain || source.domain || getSafeDomain(source.url, '');
  const faviconUrl = getFaviconUrl(linkUrl, metadata?.favicon);

  // Internal note links use a note icon; external links use favicon
  const faviconOrFallback = faviconUrl ? (
    <Image
      src={faviconUrl}
      alt=""
      w={5}
      h={5}
      loading="lazy"
      decoding="async"
      fallback={<MaterialIcon name="language" boxSize={4} color="text.muted" />}
    />
  ) : (
    <MaterialIcon name="language" boxSize={4} color="text.muted" />
  );

  const externalIconContent = isLoading ? (
    <Skeleton w={5} h={5} borderRadius="sm" />
  ) : (
    faviconOrFallback
  );

  const iconContent = internal ? <BowerIcon name="note" size="16px" /> : externalIconContent;

  return (
    <Popover
      trigger="hover"
      placement="top"
      openDelay={100}
      closeDelay={200}
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
    >
      <PopoverTrigger>
        <ChakraLink
          href={linkUrl}
          isExternal={!internal}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w={4}
          h={4}
          fontSize="2xs"
          // The badge IS the anchor, so the global
          // `a:not(.chakra-button):not([data-link-plain])` rule owns its weight
          // and paints `medium`. Nothing local is declared here because nothing
          // local would land: a style prop is (0,1,0) and the `&&` trick that
          // used to sit here is (0,2,0), both short of the rule's (0,2,1) — the
          // doubled class was measured rendering 600 from the GLOBAL rule, not
          // from itself. It read as working only because Suisse Int'l ships 400
          // and 600 only, so `medium` and `semibold` are one face. If the badge
          // ever needs a weight of its own it wants `data-link-plain`, not a
          // deeper `&`.
          bg="brand.surface"
          borderRadius="full"
          verticalAlign="super"
          lineHeight="1"
          ml={0.5}
          _hover={{ bg: 'brand.muted', transform: 'scale(1.1)' }}
          transition="all 0.15s"
        >
          {num}
        </ChakraLink>
      </PopoverTrigger>
      <PopoverContent
        w="auto"
        maxW="280px"
        // `bg.elevated`, not `bg.surface`. This citation card floats above the
        // chat panel, and since the 2026-09 flip `bg.surface` IS the fill of
        // the things it floats over — the panel reads `frame.panel` and the
        // user bubble reads `bg.surface`, so a parchment card over either had
        // nothing but its hairline to separate it. Same rule, and the same
        // reason, as the Modal and Menu themes.
        bg="bg.elevated"
        borderColor="border.default"
        shadow="lg"
        _focus={{ outline: 'none' }}
      >
        {/* The arrow is a separate element and takes the same fill — a
            `bg.elevated` card with a `bg.surface` arrow reads as a torn edge. */}
        <PopoverArrow bg="bg.elevated" />
        <PopoverBody p={3}>
          <ChakraLink href={linkUrl} isExternal={!internal}>
            <HStack spacing={3} align="flex-start">
              <Box
                flexShrink={0}
                w={8}
                h={8}
                borderRadius="md"
                bg="bg.muted"
                display="flex"
                alignItems="center"
                justifyContent="center"
                overflow="hidden"
              >
                {iconContent}
              </Box>
              <VStack align="flex-start" spacing={0.5} flex={1} minW={0}>
                {isLoading && !internal ? (
                  <SkeletonText noOfLines={2} spacing={1} skeletonHeight={3} w="150px" />
                ) : (
                  <>
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color="text.primary"
                      noOfLines={2}
                      lineHeight="1.3"
                    >
                      {getDisplayTitle(displayTitle)}
                    </Text>
                    {!internal && displayDomain && (
                      <Text fontSize="xs" color="text.muted" noOfLines={1} textDecoration="none">
                        {displayDomain}
                      </Text>
                    )}
                  </>
                )}
              </VStack>
            </HStack>
          </ChakraLink>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

function CitationBadge({
  num,
  source,
  internalLinkPolicy,
}: {
  readonly num: number;
  readonly source: GroundingSource;
  readonly internalLinkPolicy: InternalLinkPolicy;
}) {
  if (internalLinkPolicy === 'label' && isInternalUrl(source.url)) {
    return <CitationLabel num={num} />;
  }
  return <NavigableCitationBadge num={num} source={source} />;
}

/**
 * Process React children to replace [n] text patterns with clickable
 * superscript citation numbers that link to the corresponding source.
 */
function processCitations(
  children: React.ReactNode,
  sourceMap: Map<number, GroundingSource>,
  internalLinkPolicy: InternalLinkPolicy,
): React.ReactNode {
  if (sourceMap.size === 0) return children;

  return React.Children.map(children, (child) => {
    if (typeof child !== 'string') return child;

    const parts = child.split(CITATION_RE);
    if (parts.length === 1) return child;

    return parts.map((part, idx) => {
      const match = CITATION_RE.exec(part) ?? part.match(/^\[(\d+)\]$/);
      if (!match || !part.match(/^\[\d+\]$/)) return part;

      const num = Number.parseInt(part.slice(1, -1), 10);
      const source = sourceMap.get(num);
      if (!source) return part;

      return (
        <CitationBadge
          key={`cite-${num}-${idx}`}
          num={num}
          source={source}
          internalLinkPolicy={internalLinkPolicy}
        />
      );
    });
  });
}

/**
 * Build react-markdown component overrides that inject citation processing.
 */
/**
 * Exported so tests drive the REAL component map instead of re-declaring a
 * lookalike. A hand-copied harness diverges silently: dropping the `node` prop
 * from the `table` entry blanks every table in every answer, and a test that
 * mirrors the wiring by hand stays green through it.
 */
export function buildMarkdownComponents(
  sourceMap: Map<number, GroundingSource>,
  internalLinkPolicy: InternalLinkPolicy = 'navigate',
  isStreaming = false,
) {
  const withCitations = (children: React.ReactNode) =>
    processCitations(children, sourceMap, internalLinkPolicy);

  return {
    p: ({ children }: { children?: React.ReactNode }) => (
      <Text mb={3} color="text.primary" fontSize="sm" lineHeight="1.7">
        {withCitations(children)}
      </Text>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => (
      <Text as="strong" fontWeight="semibold" color="text.primary">
        {withCitations(children)}
      </Text>
    ),
    em: ({ children }: { children?: React.ReactNode }) => (
      <Text as="em" fontStyle="italic" color="text.primary">
        {withCitations(children)}
      </Text>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
      <Box as="ul" pl={5} mb={3} sx={{ listStyleType: 'disc', '& > li': { mb: 2 } }}>
        {children}
      </Box>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
      <Box as="ol" pl={5} mb={3} sx={{ listStyleType: 'decimal', '& > li': { mb: 2 } }}>
        {children}
      </Box>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
      <Box as="li" color="text.primary" fontSize="sm" lineHeight="1.7" display="list-item">
        {withCitations(children)}
      </Box>
    ),
    // EVERY fenced block is routed from here, off the hast node — `pre` is the
    // structural signal for "this is a block", so nothing has to be inferred
    // from a className or a newline. `children` is deliberately unused on the
    // fenced path: the block components (chatBlocks.tsx, chatCode.tsx) own
    // their containers, and the rendered children would be the inline chip
    // below.
    pre: ({ node, children }: { node?: HastElement; children?: React.ReactNode }) => {
      const fence = readFencedCode(node);
      if (!fence) {
        // No <code> child — not something this override understands. Keep the
        // old scroller so an unexpected <pre> still renders sanely.
        return (
          <Box overflowX="auto" my={3}>
            {children}
          </Box>
        );
      }

      // Compared against the RAW hint, so existing ```chart fences are
      // untouched — including mid-stream, where ChartBlock already falls back
      // gracefully on a half-arrived spec.
      if (fence.hint === 'chart') {
        return <ChatChartBlock spec={fence.code.trim()} />;
      }

      // Unlike the `chart` branch above, this one is gated on `!isStreaming`:
      // ChartBlock degrades gracefully on a half-arrived spec by itself, but
      // MermaidBlock does not. Every streamed token changes `code`, so an
      // ungated branch re-ran mermaid's full parse/layout/render pipeline on
      // the main thread once per token and fired `Mermaid Parse Failed` on
      // every one of those runs. Gating here means a half-streamed fence
      // falls through to the `isStreaming` plain-code block below instead,
      // and the diagram renders once, when the turn completes.
      if (fence.hint === 'mermaid' && !isStreaming) {
        return <ChatMermaidBlock code={fence.code.trim()} />;
      }

      // Mid-stream the closing fence hasn't arrived yet, and react-markdown
      // renders an unclosed fence as a code block anyway — highlighting here
      // would re-parse the whole block on every token and the user would watch
      // it recolour character by character. Plain until the turn completes.
      if (isStreaming) {
        return (
          <Box
            as="pre"
            my={3}
            px={3}
            py={2}
            bg="code.bg"
            color="code.text"
            borderRadius="10px"
            overflowX="auto"
            fontFamily="mono"
            fontSize="xs"
            lineHeight="tall"
          >
            {fence.code}
          </Box>
        );
      }

      return (
        <ChatCodeBlock code={fence.code} language={fence.hint} infoString={fence.infoString} />
      );
    },
    // Chat images are model-authored, so the src is untrusted input. Only our
    // own attachment path renders; anything else is dropped rather than
    // shown, because there is no CSP to fall back on. See figureSrc.ts.
    img: ({ src, alt }: { src?: string; alt?: string }) => {
      const figure = parseFigureSrc(src);
      if (!figure) return null;
      return (
        <ChatFigureBlock attachmentId={figure.attachmentId} caption={alt?.trim() || 'Figure'} />
      );
    },
    // Inline only. A fenced block never reaches here: `pre` above renders from
    // the tree and drops its children.
    //
    // `code.bg` / `code.text`, NOT `bg.muted`: the two are no longer the same
    // colour (code.bg is `surface.400` #F4F2EE, bg.muted is `neutral.200`
    // #E5E3DE), so the chip was a full ramp step darker than the fenced block
    // and the streaming `pre` above — a heavy grey lozenge in the middle of a
    // sentence. The lighter parchment reads as a wash rather than a highlight.
    //
    // The same recipe is declared at four other places and they were moved
    // together, because one product answering "inline code" five ways is the
    // drift this is meant to close: the themed Chakra `Code`
    // (`theme/components.ts`), the TipTap editor
    // (`theme/styles/tiptap-editor.css`) and the two AttachmentDetail markdown
    // renderers. Radius is `md` at all five.
    code: ({ children }: { children?: React.ReactNode }) => (
      <Text
        color="code.text"
        as="code"
        bg="code.bg"
        px={1.5}
        py={0.5}
        borderRadius="md"
        fontSize="xs"
        fontFamily="mono"
      >
        {children}
      </Text>
    ),
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
      // An entity gets the etched icon + hover preview; any other in-app link
      // (a list page, say) keeps the plain internal treatment; the web gets the
      // trailing open_in_new. The three must stay visually distinct.
      const entity = href ? parseEntityHref(href) : null;
      if (entity && href) {
        return (
          <EntityLink href={href} entity={entity} internalLinkPolicy={internalLinkPolicy}>
            {withCitations(children)}
          </EntityLink>
        );
      }
      if (href && isInternalUrl(href)) {
        if (internalLinkPolicy === 'label') {
          return (
            <Text as="span" fontWeight="medium" color="text.primary">
              {withCitations(children)}
            </Text>
          );
        }
        return (
          <ChakraLink as={RouterLink} to={href}>
            {withCitations(children)}
          </ChakraLink>
        );
      }
      return <ExternalLink href={href}>{children}</ExternalLink>;
    },
    h1: ({ children }: { children?: React.ReactNode }) => (
      <Text as="h3" fontSize="md" fontWeight="semibold" color="text.primary" mb={2} mt={3}>
        {withCitations(children)}
      </Text>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
      <Text as="h4" fontSize="sm" fontWeight="semibold" color="text.primary" mb={2} mt={3}>
        {withCitations(children)}
      </Text>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
      <Text as="h5" fontSize="sm" fontWeight="semibold" color="text.primary" mb={1} mt={2}>
        {withCitations(children)}
      </Text>
    ),
    // The frame + column presentation come from the hast node (MarkdownTable);
    // the CELLS stay ReactMarkdown's, so links, emphasis, code and citation
    // markers inside a cell keep working. Rendering cells from the flattened
    // node dropped all of that silently.
    table: ({ node, children }: { node?: HastElement; children?: React.ReactNode }) => (
      <MarkdownTable node={node}>{children}</MarkdownTable>
    ),
    thead: ({ children }: { children?: React.ReactNode }) => <Box as="thead">{children}</Box>,
    tbody: ({ children }: { children?: React.ReactNode }) => <Box as="tbody">{children}</Box>,
    tr: ({ children }: { children?: React.ReactNode }) => <Box as="tr">{children}</Box>,
    th: ({ children }: { children?: React.ReactNode }) => (
      <Box as="th" scope="col">
        {withCitations(children)}
      </Box>
    ),
    td: ({ children }: { children?: React.ReactNode }) => {
      // Only a cell that is plain text can be a status — one carrying a link
      // or emphasis is content, not a state, and must render as-is.
      const tone = typeof children === 'string' ? statusToneFor(children) : null;
      return (
        <Box as="td">
          {tone ? <StatusPill value={children as string} tone={tone} /> : withCitations(children)}
        </Box>
      );
    },
  };
}

/** In-memory cache of submitted feedback — survives remounts within the same page session. */
const feedbackCache = new Map<string, 'up' | 'down'>();

function MessageFeedback({ traceId }: { traceId: string }) {
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(
    () => feedbackCache.get(traceId) ?? null,
  );
  const [submitting, setSubmitting] = useState(false);

  const handleFeedback = (value: 'up' | 'down') => {
    if (submitting) return;
    // Clicking the already-selected thumb is a no-op
    if (feedback === value) return;
    setSubmitting(true);
    setFeedback(value);
    feedbackCache.set(traceId, value);
    const score = value === 'up' ? 1 : 0;
    void submitChatFeedback(traceId, score).then((ok) => {
      setSubmitting(false);
      if (!ok) {
        setFeedback(null);
        feedbackCache.delete(traceId);
        return;
      }
      // Tracked on confirmed success, not on click. The agent service can
      // refuse a rating (it verifies the trace belongs to the caller's
      // workspace before scoring it), and an event fired on click is never
      // retracted — which would leave PostHog counting ratings Langfuse never
      // received, and the #customers thumbs-down alert firing for them.
      safeAnalytics.track(ANALYTICS_EVENTS.CHAT_FEEDBACK_SUBMITTED, {
        score,
        value,
        // The Langfuse trace this rating scores. Without it a thumbs-down in
        // PostHog can never be resolved back to the response it was about —
        // and the #customers Slack alert has nothing to deep-link to.
        trace_id: traceId,
      });
    });
  };

  return (
    <HStack spacing={0.5} mt={1}>
      <IconButton
        aria-label="Good response"
        size="xs"
        variant="ghost"
        icon={
          <MaterialIcon
            name="thumb_up"
            boxSize={3.5}
            filled={feedback === 'up'}
            color={feedback === 'up' ? 'brand.onSurface' : 'text.muted'}
          />
        }
        isDisabled={submitting}
        onClick={() => handleFeedback('up')}
        minW={6}
        h={6}
        _hover={{ bg: 'bg.hover' }}
      />
      <IconButton
        aria-label="Bad response"
        size="xs"
        variant="ghost"
        icon={
          <MaterialIcon
            name="thumb_down"
            boxSize={3.5}
            filled={feedback === 'down'}
            color={feedback === 'down' ? 'text.error' : 'text.muted'}
          />
        }
        isDisabled={submitting}
        onClick={() => handleFeedback('down')}
        minW={6}
        h={6}
        _hover={{ bg: 'bg.hover' }}
      />
    </HStack>
  );
}

export function messageHasInternalReference(message: ChatMessage): boolean {
  if (message.sources?.some((source) => isInternalUrl(source.url))) return true;
  let cursor = 0;
  while (cursor < message.content.length) {
    const linkStart = message.content.indexOf('](', cursor);
    if (linkStart === -1) return false;
    const linkEnd = message.content.indexOf(')', linkStart + 2);
    if (linkEnd === -1) return false;
    const destination = message.content
      .slice(linkStart + 2, linkEnd)
      .trim()
      .split(/\s/, 1)[0];
    if (destination && isInternalUrl(destination)) return true;
    cursor = linkEnd + 1;
  }
  return false;
}

/** Stable identity so the non-user branch of the segments memo can't itself
 * become a re-render trigger. */
const EMPTY_SEGMENTS: readonly FencedSegment[] = [];

export function BirdMessage({
  message,
  internalLinkPolicy = 'navigate',
  onRespondToConfirmation,
  respondingConfirmationId = null,
  onRespondToChoice,
  respondingChoiceId = null,
}: BirdMessageProps) {
  const isUser = message.role === 'user';

  // Replies render LaTeX through rehype-katex: fetch its stylesheet on demand
  // (see utils/katexStyles.ts for why it is not a static import).
  useEffect(() => {
    void loadKatexStyles();
  }, []);

  // Build a Map<number, source> for fast citation lookups.
  const sourceMap = useMemo(() => {
    const map = new Map<number, GroundingSource>();
    if (message.sources) {
      for (const source of message.sources) {
        map.set(source.index, source);
      }
    }
    return map;
  }, [message.sources]);

  const isStreaming = message.deliveryStatus === 'streaming';

  // `isStreaming` MUST stay in the dep array: without it the map is built once
  // mid-stream and the finished block stays plain forever.
  const markdownComponents = useMemo(
    () => buildMarkdownComponents(sourceMap, internalLinkPolicy, isStreaming),
    [internalLinkPolicy, sourceMap, isStreaming],
  );

  // Read once so the render below is not a chain of non-null assertions.
  const pendingConfirmations = message.pendingConfirmations;
  const pendingChoices = message.pendingChoices;

  // Preprocess content to fix citation placement issues, then put each `$$`
  // of a multi-line display block on its own line (see mathDelimiters.ts).
  const processedContent = useMemo(
    () => normalizeDisplayMathDelimiters(preprocessCitationPlacement(message.content)),
    [message.content],
  );

  /**
   * User turns only. BirdChatPanel maps its messages without React.memo, so
   * every message re-renders on each streamed token — and a user turn is
   * exactly where a long pasted script lives, so an unmemoised scan here is a
   * full regex pass over that script per token.
   *
   * Gated on `isUser` rather than hoisted unconditionally: the ASSISTANT's
   * content is what changes each token, so scanning it (only to discard the
   * result — Bower's turns render through react-markdown) would reintroduce the
   * per-token cost this memo exists to remove.
   */
  const userSegments = useMemo(
    () => (isUser ? splitFencedSegments(message.content) : EMPTY_SEGMENTS),
    [isUser, message.content],
  );

  if (message.role === 'status') {
    return <BirdStatusLine message={message} />;
  }

  if (isUser) {
    return (
      <Box display="flex" justifyContent="flex-end" minW={0} mb={3}>
        {/* Parchment, no hairline. Bower's turns carry no bubble at all, so
            this is the only filled thing in the thread — it can afford to be
            quiet, and staying quiet is what lets it survive the long pasted
            text people actually put in that field.

            It was a twilight wash with a hairline, which was the right call
            while the panel itself was parchment: the bubble needed a tint to
            separate from its background. The panel went white in the 2026-09
            frame pass, so parchment now separates on its own and the accent
            can go back to meaning AI rather than "your turn".

            The corner is asymmetric on the BOTTOM RIGHT — the edge nearest the
            composer the text came from. */}
        <Box {...USER_BUBBLE} minW={0}>
          {message.content &&
            userSegments.map((segment, index) =>
              segment.kind === 'code' ? (
                <ChatCodeBlock
                  key={`seg-${index}`}
                  code={segment.body}
                  language={segment.language}
                  infoString={segment.language}
                />
              ) : (
                <Text
                  key={`seg-${index}`}
                  whiteSpace="pre-wrap"
                  overflowWrap="anywhere"
                  fontSize="sm"
                  color="text.primary"
                >
                  {segment.body}
                </Text>
              ),
            )}
          {message.attachments && message.attachments.length > 0 && (
            <MessageAttachments attachments={message.attachments} />
          )}
          <Text fontSize="xs" color="text.muted" mt={1} textAlign="right">
            {formatTime(message.timestamp)}
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box minW={0} mb={3}>
      <Box w="full" minW={0} maxW="95%" fontSize="sm" lineHeight="1.6">
        <ReactMarkdown
          remarkPlugins={CHAT_REMARK_PLUGINS}
          rehypePlugins={CHAT_REHYPE_PLUGINS}
          components={markdownComponents}
        >
          {processedContent}
        </ReactMarkdown>
      </Box>
      {/* Rendered below the answer, because the answer IS the preview — the
          assistant has just listed what it intends to act on. */}
      {/* One prompt per paused call — a turn can pause more than one. */}
      {onRespondToConfirmation &&
        pendingConfirmations?.map((confirmation) => (
          <ToolConfirmationPrompt
            key={confirmation.id}
            confirmation={confirmation}
            isResponding={respondingConfirmationId === confirmation.id}
            onRespond={(confirmed) => onRespondToConfirmation(confirmation.id, confirmed)}
          />
        ))}
      {/* Same placement, same reason: the prose above is what the researcher
          reads in order to choose. */}
      {onRespondToChoice &&
        pendingChoices?.map((choice) => (
          <ToolChoicePrompt
            key={choice.id}
            choice={choice}
            isResponding={respondingChoiceId === choice.id}
            onRespond={(value) => onRespondToChoice(choice.id, value)}
          />
        ))}
      {/* One card per apply batch, built from the tool result (P2-D4). */}
      {message.spreadsheetChanges?.map((card, i) => (
        <AgentSpreadsheetChangeCard
          key={`${card.artifactId}-${i}`}
          card={card}
          internalLinkPolicy={internalLinkPolicy}
        />
      ))}
      {message.sources && message.sources.length > 0 && (
        <MessageSources sources={message.sources} internalLinkPolicy={internalLinkPolicy} />
      )}
      {message.traceId && message.content && <MessageFeedback traceId={message.traceId} />}
    </Box>
  );
}
